<script setup>
import { ref, watch, computed } from 'vue'
import { Head, router, useForm, usePage } from '@inertiajs/vue3'
import { PhBook } from "@phosphor-icons/vue"
import Layout from '@pages/Shared/layouts/AdminLayout.vue'
import Modal from '@pages/Shared/Modal.vue'
import Input from '@pages/Shared/form/Input.vue'
import MultiSelect from '@pages/Shared/form/MultiSelect.vue'
import Prompt from '@pages/Shared/Prompt.vue'
import Table from '@pages/Shared/ui/Table.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'
import { flashToast } from "@/toast.js"
import { capitalize } from '@/helpers.js'

const props = defineProps({
    subjects: Object,
    sections: Array,
    filters:  Object,
})

const openModal         = ref(false)
const modalStatus       = ref(null)
const modalTitle        = ref('')
const showPrompt        = ref(false)
const selectedSections  = ref([])
const selectedClasses   = ref([])

const filters = ref({ search: null, section: null, class: null, ...props.filters })

const page = usePage()
const can  = computed(() => page.props?.can || {})

const form = useForm({
    _method:  null,
    id:       null,
    name:     '',
    arabic:   '',
    sections: [],
    classes:  [],
})

const columns = [
    { key: 'index',    label: '#',                  type: 'index'  },
    { key: 'name',     label: 'Name'                               },
    { key: 'arabic',   label: 'Arabic Name'                        },
    { key: 'sections', label: 'Sections Offering',  type: 'slot'   },
    { key: 'classes',  label: 'Classes Offering',   type: 'slot'   },
    { key: 'actions',  label: '',                   type: 'actions'},
]

const filterClassOptions = computed(() => {
    if (!filters.value.section) return []
    const section = props.sections.find(s => s.id === filters.value.section)
    return section?.classes?.map(c => ({ label: c.name, value: c.id })) ?? []
})

const classOptions = computed(() => {
    if (!selectedSections.value.length) return []
    return props.sections
        .filter(sec => selectedSections.value.includes(sec.id))
        .flatMap(sec => sec.classes || [])
        .map(c => ({ label: c.name, value: c.id }))
})

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

watch(selectedSections, () => {
    selectedClasses.value = selectedClasses.value.filter(c =>
        classOptions.value.some(co => co.value === c)
    )
})

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined)
    )
    router.get('/admin/subjects', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value) => { filters.value.search = value; applyFilters() }

const handleFilter = (newFilters) => {
    const sectionChanged = Object.prototype.hasOwnProperty.call(newFilters, 'section') &&
                           newFilters.section !== filters.value.section
    filters.value = { ...filters.value, ...newFilters }
    if (sectionChanged) filters.value.class = null
    applyFilters()
}

const setFormData = (subject) => {
    form.id            = subject.id
    form.name          = subject.name
    form.arabic        = subject.arabic
    selectedSections.value = subject.sections.map(s => s.id)
    selectedClasses.value  = subject.classes.map(c => c.id)
}

const editSubject = (id) => {
    const subject = props.subjects.data.find(s => s.id === id)
    if (!subject) return
    setFormData(subject)
    modalTitle.value  = 'Edit Subject'
    modalStatus.value = 'edit'
    openModal.value   = true
}

const viewSubject = (id) => {
    const subject = props.subjects.data.find(s => s.id === id)
    if (!subject) return
    setFormData(subject)
    modalTitle.value  = 'View Subject'
    modalStatus.value = 'view'
    openModal.value   = true
}

const openAddSubjectModal = () => {
    form.reset()
    form.clearErrors()
    selectedSections.value = []
    selectedClasses.value  = []
    modalTitle.value  = 'Add Subject'
    modalStatus.value = 'create'
    openModal.value   = true
}

const handleSubmit = () => {
    form.sections = [...selectedSections.value]
    form.classes  = [...selectedClasses.value]

    const options = {
        onSuccess: (page) => {
            form.clearErrors()
            form.reset()
            selectedSections.value = []
            selectedClasses.value  = []
            openModal.value = false
            flashToast(page.props.flash)
        }
    }

    if (modalStatus.value === 'edit') {
        form._method = 'put'
        form.post(`/admin/subjects/${form.id}`, options)
    } else {
        form.post('/admin/subjects', options)
    }
}

const deleteSubject = (id) => { form.id = id; showPrompt.value = true }

const confirmDelete = () => {
    form.delete(`/admin/subjects/${form.id}`, {
        onSuccess: (page) => {
            showPrompt.value = false
            form.clearErrors()
            form.reset()
            flashToast(page.props.flash)
        }
    })
}

const handleModalClose = () => {
    modalStatus.value      = null
    modalTitle.value       = null
    openModal.value        = false
    selectedSections.value = []
    selectedClasses.value  = []
    form.clearErrors()
    form.reset()
}
</script>

<template>
    <Layout>
        <Head title="Subjects Management" />

        <Prompt
            title="subject"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle"
            @close="handleModalClose"
        >
            <form class="p-4 md:p-5" @submit.prevent="handleSubmit">

                <Input v-model="form.name" type="text" label="Name" placeholder="Subject Name"
                    :disabled="modalStatus === 'view'" :error="form.errors.name" class="mb-4" />

                <Input v-model="form.arabic" type="text" label="Arabic" placeholder="Arabic Name"
                    :disabled="modalStatus === 'view'" :error="form.errors.arabic" class="mb-4" />

                <MultiSelect
                    v-model="selectedSections"
                    :options="sections.map(s => ({ label: s.name, value: s.id }))"
                    label="Sections" placeholder="Select sections"
                    :disabled="modalStatus === 'view'" class="mb-4" />

                <MultiSelect
                    v-model="selectedClasses"
                    :options="classOptions"
                    label="Classes" placeholder="Select class(es)"
                    :disabled="modalStatus === 'view'" class="mb-4" />

                <button
                    v-if="modalStatus !== 'view'"
                    type="submit"
                    class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold
                           transition flex justify-center items-center gap-2"
                    :class="modalStatus === 'edit'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-primary hover:bg-primary-light'"
                    :disabled="form.processing"
                >
                    <Spinner v-if="form.processing" :size="14" />
                    {{ modalStatus === 'edit' ? 'Update Subject' : 'Add Subject' }}
                </button>
            </form>
        </Modal>

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Subjects</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all subjects</p>
            </div>
            <button
                v-if="can.createSubject"
                @click="openAddSubjectModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhBook :size="20" />
                Add Subject
            </button>
        </div>

        <Table
            :data="subjects"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            :filters="[
                {
                    key: 'section', label: 'Section',
                    options: [
                        { label: 'All Sections', value: null },
                        ...sections.map(s => ({ label: s.name, value: s.id })),
                    ],
                    value: filters.section,
                },
                {
                    key: 'class', label: 'Class',
                    options: [
                        { label: 'All Classes', value: null },
                        ...filterClassOptions,
                    ],
                    value: filters.class,
                    disabled: !filters.section,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @edit="editSubject"
            @view="viewSubject"
            @delete="deleteSubject"
        >
            <template #sections="{ row }">
                <div class="flex flex-wrap gap-1">
                    <template v-if="row.sections?.length">
                        <span
                            v-for="section in row.sections" :key="section.id"
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                   bg-primary-50 text-primary rounded-md border border-primary-100"
                        >
                            {{ capitalize(section.name) }}
                        </span>
                    </template>
                    <span v-else class="text-xs text-gray-400">N/A</span>
                </div>
            </template>

            <template #classes="{ row }">
                <div class="flex flex-wrap gap-1">
                    <template v-if="row.classes?.length">
                        <span
                            v-for="cls in row.classes" :key="cls.id"
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                   bg-indigo-50 text-indigo-700 rounded-md border border-indigo-100"
                        >
                            {{ capitalize(cls.name) }}
                        </span>
                    </template>
                    <span v-else class="text-xs text-gray-400">N/A</span>
                </div>
            </template>
        </Table>
    </Layout>
</template>